import React, { Fragment, StrictMode, useEffect, useRef, useState } from "react";
import { Image } from "@App/types";
import { useAssetUrl } from "@App/hooks/useAssetUrl";
import { GarageHasLessImages, GarageHasOneImage } from "@App/utils/conditionals/garages";

type ImageSliderProps = {
  images?: Image[];
};

export const ImageSlider = React.memo<ImageSliderProps>((props: ImageSliderProps) => {
  const { url } = useAssetUrl();

  const maxScrollWidth = useRef(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const carousel = useRef(null);

  const movePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prevState) => prevState - 1);
    }
  };

  const moveNext = () => {
    if (
      carousel.current !== null &&
      carousel.current.offsetWidth * currentIndex <= maxScrollWidth.current
    ) {
      setCurrentIndex((prevState) => prevState + 1);
    }
  };

  const isDisabled = (direction) => {
    if (direction === "prev") {
      return currentIndex <= 0;
    }

    if (direction === "next" && carousel.current !== null) {
      return carousel.current.offsetWidth * currentIndex >= maxScrollWidth.current;
    }

    return false;
  };

  useEffect(() => {
    if (carousel !== null && carousel.current !== null) {
      carousel.current.scrollLeft = carousel.current.offsetWidth * currentIndex;
    }
  }, [currentIndex]);

  useEffect(() => {
    maxScrollWidth.current = carousel.current
      ? carousel.current.scrollWidth - carousel.current.offsetWidth
      : 0;
  }, []);

  return (
    <StrictMode>
      <div className="carousel my-12 mx-auto h-64">
        <div className="relative overflow-hidden h-full">
          <div
            className={`
                            flex justify-between absolute top left w-full h-full
                            ${GarageHasLessImages(props.images) && "md:hidden"}
                            ${GarageHasOneImage(props.images) ? "hidden" : "block"}
                        `}
          >
            <button
              onClick={movePrev}
              className="hover:bg-blue-900/75 text-white w-10 h-full text-center opacity-75 hover:opacity-100 disabled:opacity-25 disabled:cursor-not-allowed z-10 p-0 m-0 transition-all ease-in-out duration-300"
              disabled={isDisabled("prev")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-20 -ml-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="var(--color-primary)"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              <span className="sr-only">Prev</span>
            </button>
            <button
              onClick={moveNext}
              className="hover:bg-blue-900/75 text-white w-10 h-full text-center opacity-75 hover:opacity-100 disabled:opacity-25 disabled:cursor-not-allowed z-10 p-0 m-0 transition-all ease-in-out duration-300"
              disabled={isDisabled("next")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-20 -ml-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="var(--color-primary)"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              <span className="sr-only">Next</span>
            </button>
          </div>
          <div
            ref={carousel}
            className="carousel-container relative flex gap-1 overflow-hidden scroll-smooth snap-x snap-mandatory touch-pan-x w-full h-full z-0"
          >
            {props.images.length <= 0 && (
              <Fragment>
                <div
                  key="1"
                  className="carousel-item text-center relative w-full md:w-1/3 h-full snap-start"
                >
                  <div
                    className="h-full w-full aspect-video block bg-origin-padding bg-left-top bg-cover bg-no-repeat z-0"
                    style={{
                      backgroundImage: `url(${url}/images/werkstatt-header-1.jpg)`,
                    }}
                  >
                    <img
                      src={`${url}//images/werkstatt-header-1.jpg` || ""}
                      className="w-full aspect-video hidden"
                    />
                  </div>
                </div>
                <div
                  key="2"
                  className="carousel-item text-center relative w-full md:w-1/3 h-full snap-start"
                >
                  <div
                    className="h-full w-full aspect-video block bg-origin-padding bg-left-top bg-cover bg-no-repeat z-0"
                    style={{
                      backgroundImage: `url(${url}/images/werkstatt-header-2.jpg)`,
                    }}
                  >
                    <img
                      src={`${url}//images/werkstatt-header-2.jpg` || ""}
                      className="w-full aspect-video hidden"
                    />
                  </div>
                </div>
                <div
                  key="3"
                  className="carousel-item text-center relative w-full md:w-1/3 h-full snap-start"
                >
                  <div
                    className="h-full w-full aspect-video block bg-origin-padding bg-left-top bg-cover bg-no-repeat z-0"
                    style={{
                      backgroundImage: `url(${url}/images/werkstatt-header-3.jpg)`,
                    }}
                  >
                    <img
                      src={`${url}/images/werkstatt-header-3.jpg` || ""}
                      className="w-full aspect-video hidden"
                    />
                  </div>
                </div>
              </Fragment>
            )}
            {props.images.length > 0 &&
              props.images.map((image) => (
                <div
                  key={image.id}
                  className="carousel-item text-center relative w-full md:w-1/3 h-full snap-start"
                >
                  <div
                    className="h-full w-full aspect-video block bg-origin-padding bg-left-top bg-cover bg-no-repeat z-0"
                    style={{
                      backgroundImage: `url(${url}/storage/${image.path})`,
                    }}
                  >
                    <img
                      src={`${url}/storage/${image.path}` || ""}
                      className="w-full aspect-video hidden"
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </StrictMode>
  );
});
